import React from 'react'
import styled from 'styled-components'

// PRO-130: the LEAF seam modules, NOT the `src/antd` barrel — this skeleton is
// the boot gate (rendered before any route chunk), so a barrel import welds the
// whole antd component barrel onto the entry chunk.
import { Skeleton } from 'src/antd/Skeleton'
import Space from 'src/antd/Space'
import { LoadingWall } from 'src/ui/loading/LoadingWall'

const HEADER_HEIGHT = 56

const SkeletonFrame = styled.div`
  min-height: calc(100vh - ${HEADER_HEIGHT}px);
  padding: 24px;
`

// FE-PERF-1 FE1.5: every lazy route used to cold-open on a bare
// centered <Spin> on white for its whole chunk load. The fallback now
// sketches the page frame every view shares - a title row, a tab bar,
// content blocks - so a cold navigation reads as "the page is coming"
// instead of "the app is thinking". Skeleton primitives only: no
// data, no strings, nothing extra to fetch before it paints.
//
// FE-PERF-2 FE2.1: extracted to its own file so the Route Studio
// registry's per-page Suspense boundaries share the exact same frame
// (one loading language platform-wide), and FE2.2's auth boot gate
// can reuse it instead of the bare boot spinner.
//
// DESIGN TOGGLE, 2026-08-21. This frame is the app's most-seen loading
// state - eight Suspense boundaries plus the boot gate - and it is the
// surface the captain reported: "every navigation flashes a wall of grey
// blocks, so the app feels slower than it is." Both halves of that are
// answered here, and only with the toggle ON: the wall becomes one quiet
// spinner, and nothing is drawn at all until LOADING_DELAY_MS has passed,
// so a chunk the browser already has renders content with no loading state
// on screen. With the toggle off this file renders exactly what it always
// did. `src/ui/loading/constants.ts` carries the threshold and the reasoning.
const RouteChunkSkeleton = () => (
  // The accessible name is the bare English literal, not `t('Loading')`: this
  // frame paints before any locale has resolved, which is the same reason the
  // file deliberately carries no strings. Every other LoadingWall translates.
  <LoadingWall minHeight={`calc(100vh - ${HEADER_HEIGHT}px)`} label="Loading">
    <SkeletonFrame aria-busy="true" data-test="route-chunk-skeleton">
      <Skeleton.Input
        active
        size="small"
        style={{ width: 240, marginBottom: 20 }}
      />
      <Space size="small" style={{ display: 'flex', marginBottom: 24 }}>
        <Skeleton.Button active size="small" />
        <Skeleton.Button active size="small" />
        <Skeleton.Button active size="small" />
        <Skeleton.Button active size="small" />
      </Space>
      <Skeleton
        active
        title={false}
        paragraph={{ rows: 3 }}
        style={{ marginBottom: 24 }}
      />
      <Skeleton active title={false} paragraph={{ rows: 6 }} />
    </SkeletonFrame>
  </LoadingWall>
)

export default RouteChunkSkeleton
